import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'
import { useSocket } from '../context/SocketContext'
import { API_FILE_URL } from '../constants/api'

// ── Helpers ───────────────────────────────────────────────────────────────────

// Parse the images field (JSON array) into a usable URL for display
const resolveUrl = (raw) => {
  if (!raw) return null
  if (raw.startsWith('http')) return raw                               // GCS or external URL
  const filename = raw.replace(/\\/g, '/').split('/').pop()
  return `${API_FILE_URL}/${filename}`                                 // local uploads fallback
}

// Returns ALL image URLs from a product's images field
const parseAllImgs = (images) => {
  try {
    const arr = typeof images === 'string' ? JSON.parse(images) : images
    if (!Array.isArray(arr)) return []
    return arr.map(resolveUrl).filter(Boolean)
  } catch { return [] }
}

// Returns just the first image URL for the grid card thumbnail
const parseImg = (images) => parseAllImgs(images)[0] ?? null
const rwf = (n) => `RWF ${Number(n ?? 0).toLocaleString()}`

// ── Shared field styles ───────────────────────────────────────────────────────
const INP = { width: '100%', height: 38, borderRadius: 8, border: '1px solid #e9e9e4', padding: '0 12px', fontSize: 13, outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif", boxSizing: 'border-box', background: '#fff' }
const LBL = { display: 'block', fontSize: 11, fontWeight: 700, color: '#111317', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }

// ── OptionRow ─────────────────────────────────────────────────────────────────
// attrDefs: array of { name, type, options[] } from the selected category.
// When present → show a select per attribute and auto-compose the label.
// When absent  → show a free-text label input.
function OptionRow({ option, index, attrDefs, onChange, onDelete }) {
  const sel = { width: '100%', height: 34, borderRadius: 7, border: '1px solid #e9e9e4', padding: '0 8px', fontSize: 12, outline: 'none', background: '#fff', fontFamily: "'Plus Jakarta Sans',sans-serif", boxSizing: 'border-box', cursor: 'pointer' }
  const num = { width: '100%', height: 34, borderRadius: 7, border: '1px solid #e9e9e4', padding: '0 8px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }
  const lbl = { fontSize: 10, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 4, textTransform: 'uppercase' }

  const hasAttrs = attrDefs && attrDefs.length > 0

  // When an attribute select changes, update attrs and rebuild the label
  const handleAttrChange = (attrName, value) => {
    const attrs = { ...(option.attrs || {}), [attrName]: value }
    const label = Object.values(attrs).filter(Boolean).join(' / ')
    onChange(index, { ...option, attrs, label })
  }

  return (
    <div style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>Option {index + 1}</span>
        <button type="button" onClick={() => onDelete(index)}
          style={{ border: 'none', background: '#fef2f2', color: '#b91c1c', borderRadius: 5, padding: '3px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✕</button>
      </div>

      {/* Category-based attribute selects OR free-text label */}
      {hasAttrs ? (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(attrDefs.length, 3)}, 1fr)`, gap: 8 }}>
          {attrDefs.map(attr => (
            <div key={attr.name}>
              <div style={lbl}>{attr.name}</div>
              {attr.type === 'select' && attr.options?.length ? (
                <select
                  value={option.attrs?.[attr.name] || ''}
                  onChange={e => handleAttrChange(attr.name, e.target.value)}
                  style={sel}>
                  <option value="">Select {attr.name}…</option>
                  {attr.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  value={option.attrs?.[attr.name] || ''}
                  onChange={e => handleAttrChange(attr.name, e.target.value)}
                  placeholder={attr.name}
                  style={sel} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div style={lbl}>Option Label</div>
          <input
            value={option.label || ''}
            onChange={e => onChange(index, { ...option, label: e.target.value })}
            placeholder="e.g. Small, Red / XL…"
            style={{ ...sel, cursor: 'text' }} />
        </div>
      )}

      {/* Auto-composed label preview when using attributes */}
      {hasAttrs && option.label && (
        <div style={{ fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
          Label: <b style={{ color: '#111317' }}>{option.label}</b>
        </div>
      )}

      {/* Price + Stock */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={lbl}>Price (RWF) *</div>
          <input type="number" min="1" value={option.price || ''}
            onChange={e => onChange(index, { ...option, price: e.target.value })}
            placeholder="0" style={num} />
        </div>
        <div>
          <div style={lbl}>Stock</div>
          <input type="number" min="0" value={option.quantity || ''}
            onChange={e => onChange(index, { ...option, quantity: e.target.value })}
            placeholder="0" style={num} />
        </div>
      </div>
    </div>
  )
}

// ── Product Modal (Add & Edit) ─────────────────────────────────────────────────
function ProductModal({ product, categories, onClose, onSaved }) {
  const isEdit = !!product?.prod_id

  const [form, setForm] = useState({
    name:         product?.name         || '',
    description:  product?.description  || '',
    category:     product?.category     || '',
    price:        product?.price        || '',
    stock:        product?.quantity     || '',
    has_variants: product?.has_variants || false,
  })
  const [options,        setOptions]        = useState(
    product?.variants?.length
      ? product.variants.map(v => ({ label: v.attributes?.label || v.attributes?.option || '', price: v.price, quantity: v.quantity }))
      : [{ label: '', price: '', quantity: '' }]
  )
  // Existing server images (URLs already stored in the DB)
  const [existingImgs,   setExistingImgs]   = useState(() => isEdit ? parseAllImgs(product.images) : [])
  // New images chosen from disk
  const [imageFiles,     setImageFiles]     = useState([])
  const [newPreviews,    setNewPreviews]     = useState([])
  const [saving,         setSaving]         = useState(false)
  const [error,          setError]          = useState('')
  const fileRef = useRef()
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const totalImageCount = existingImgs.length + imageFiles.length

  // Derive attribute definitions from the currently selected category
  const selCat  = categories.find(c => c.name?.toLowerCase().trim() === form.category?.toLowerCase().trim())
  const attrDefs = (() => {
    try { return Array.isArray(selCat?.attribute_defs) ? selCat.attribute_defs : JSON.parse(selCat?.attribute_defs || '[]') }
    catch { return [] }
  })()

  // When category changes, reset options so stale attribute values don't carry over
  const prevCategoryRef = useRef(form.category)
  useEffect(() => {
    if (prevCategoryRef.current !== form.category) {
      prevCategoryRef.current = form.category
      setOptions([{ label: '', attrs: {}, price: '', quantity: '' }])
    }
  }, [form.category])

  const addOption    = () => setOptions(o => [...o, { label: '', attrs: {}, price: '', quantity: '' }])
  const updateOption = (i, val) => setOptions(o => o.map((x, idx) => idx === i ? val : x))
  const deleteOption = (i) => setOptions(o => o.filter((_, idx) => idx !== i))

  const handleImages = (e) => {
    const files = Array.from(e.target.files || [])
    const slots = 5 - totalImageCount
    const toAdd = files.slice(0, slots)
    setImageFiles(prev => [...prev, ...toAdd])
    setNewPreviews(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))])
    e.target.value = ''
  }
  const removeExisting = (i) => setExistingImgs(prev => prev.filter((_, idx) => idx !== i))
  const removeNew = (i) => {
    URL.revokeObjectURL(newPreviews[i])
    setImageFiles(prev => prev.filter((_, idx) => idx !== i))
    setNewPreviews(prev => prev.filter((_, idx) => idx !== i))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim())       { setError('Product name is required.'); return }
    if (!form.description.trim()) { setError('Product description is required.'); return }
    if (totalImageCount === 0) { setError('At least one image is required.'); return }
    if (!form.has_variants) {
      if (!form.price || Number(form.price) <= 0) { setError('A valid price is required.'); return }
    } else {
      if (!options.length)                                         { setError('Add at least one option.'); return }
      if (options.some(o => !o.label?.trim()))                    { setError('Every option needs a label.'); return }
      if (options.some(o => !o.price || Number(o.price) <= 0))   { setError('Every option needs a valid price.'); return }
    }

    setSaving(true); setError('')
    try {
      const fd = new FormData()
      fd.append('name',         form.name.trim())
      fd.append('description',  form.description.trim())
      fd.append('category',     form.category)
      fd.append('product_type', 'standard')
      fd.append('has_variants', String(form.has_variants))

      if (form.has_variants) {
        const minPrice   = Math.min(...options.map(o => Number(o.price) || 0))
        const totalStock = options.reduce((s, o) => s + (Number(o.quantity) || 0), 0)
        fd.append('price',    minPrice)
        fd.append('stock',    totalStock)
        fd.append('variants', JSON.stringify(options.map(o => ({
          attributes: { label: o.label.trim() },
          price:      Number(o.price)    || 0,
          quantity:   Number(o.quantity) || 0,
        }))))
      } else {
        fd.append('price', Number(form.price))
        fd.append('stock', Number(form.stock) || 0)
      }

      // Send existing image URLs to keep (backend merges with new uploads)
      if (isEdit) fd.append('existingPaths', JSON.stringify(existingImgs))
      imageFiles.forEach(f => fd.append('images', f))

      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } }
      const res = isEdit
        ? await api.put(`/products/update/${product.prod_id}`, fd, cfg)
        : await api.post('/products/add', fd, cfg)

      if (isEdit) {
        // Update endpoint returns { message, locked } — no product body.
        // Reconstruct from the original product merged with the edited fields.
        onSaved({
          ...product,
          name:         form.name.trim(),
          description:  form.description.trim(),
          category:     form.category,
          price:        form.has_variants
            ? Math.min(...options.map(o => Number(o.price) || 0))
            : Number(form.price),
          quantity:     form.has_variants
            ? options.reduce((s, o) => s + (Number(o.quantity) || 0), 0)
            : Number(form.stock) || 0,
          has_variants: form.has_variants,
          product_type: 'standard',
        })
      } else {
        onSaved(res.data.product || res.data)
      }
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save product.')
    } finally { setSaving(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 600 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 560, maxHeight: '92vh', background: '#fff', borderRadius: 16, zIndex: 601, display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.18)', overflow: 'hidden', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{isEdit ? '✏️ Edit Product' : '📦 Add Product'}</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <form id="productForm" onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* ── Images ── */}
            <div>
              <label style={LBL}>
                Images *
                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: totalImageCount === 0 ? '#b91c1c' : '#15803d' }}>
                  {totalImageCount}/5
                </span>
              </label>
              <div style={{ background: '#f9f9f7', border: `1px solid ${totalImageCount === 0 ? '#fecaca' : '#e9e9e4'}`, borderRadius: 10, padding: '14px' }}>

                {/* Existing server images */}
                {existingImgs.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 6 }}>Saved Images</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {existingImgs.map((url, i) => (
                        <div key={url} style={{ position: 'relative' }}>
                          <img src={url} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '2px solid #bbf7d0', display: 'block' }}
                            onError={e => { e.target.style.opacity = '0.3'; e.target.style.border = '2px solid #fecaca' }} />
                          <button type="button" onClick={() => removeExisting(i)}
                            style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New images queued for upload */}
                {newPreviews.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 6 }}>New Images</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {newPreviews.map((url, i) => (
                        <div key={i} style={{ position: 'relative' }}>
                          <img src={url} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '2px solid #bae6fd', display: 'block' }} />
                          <button type="button" onClick={() => removeNew(i)}
                            style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add button */}
                {totalImageCount < 5 && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: totalImageCount === 0 ? '#e8440a' : '#f3f3f0', color: totalImageCount === 0 ? '#fff' : '#374151', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: totalImageCount === 0 ? 'none' : '1px dashed #d1d5db' }}>
                    📷 {totalImageCount === 0 ? 'Add Images *' : `Add More (${5 - totalImageCount} left)`}
                    <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple onChange={handleImages} style={{ display: 'none' }} />
                  </label>
                )}
                {totalImageCount === 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#b91c1c', fontWeight: 600 }}>⚠ At least one image is required</div>
                )}
              </div>
            </div>

            {/* ── Name ── */}
            <div>
              <label style={LBL}>Product Name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Summer Dress" style={INP} />
            </div>

            {/* ── Description ── */}
            <div>
              <label style={LBL}>Description *</label>
              <textarea required value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe your product — material, size, colour, usage…"
                style={{ ...INP, height: 72, padding: '8px 12px', resize: 'vertical' }} />
            </div>

            {/* ── Category ── */}
            <div>
              <label style={LBL}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...INP, cursor: 'pointer' }}>
                <option value="">Select category…</option>
                {categories.map(c => (
                  <option key={c.category_id || c.name} value={c.name}>{c.icon} {c.name}</option>
                ))}
              </select>
            </div>


            {/* ── Product structure ── */}
            <div>
              <label style={LBL}>Product Structure</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { val: false, label: '📦 Single Product', desc: 'One price + stock' },
                  { val: true,  label: '🎨 Has Options',    desc: 'Sizes / colors etc.' },
                ].map(opt => (
                  <div key={String(opt.val)} onClick={() => set('has_variants', opt.val)}
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${form.has_variants === opt.val ? '#e8440a' : '#e9e9e4'}`, background: form.has_variants === opt.val ? '#fff3f0' : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.12s' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: form.has_variants === opt.val ? '#e8440a' : '#374151' }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Single product: price + stock ── */}
            {!form.has_variants && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={LBL}>Price (RWF) *</label>
                  <input type="number" min="1" required={!form.has_variants} value={form.price} onChange={e => set('price', e.target.value)} placeholder="0" style={INP} />
                </div>
                <div>
                  <label style={LBL}>Stock Quantity</label>
                  <input type="number" min="0" value={form.stock} onChange={e => set('stock', e.target.value)} placeholder="0" style={INP} />
                </div>
              </div>
            )}

            {/* ── Options ── */}
            {form.has_variants && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ ...LBL, marginBottom: 0 }}>
                    Options ({options.length})
                    {attrDefs.length > 0 && (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: '#e8440a', textTransform: 'none', letterSpacing: 0 }}>
                        — based on {selCat?.name} attributes
                      </span>
                    )}
                  </label>
                  <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                    Total stock: {options.reduce((s, o) => s + (Number(o.quantity) || 0), 0)} units
                  </span>
                </div>

                {!form.category && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '9px 13px', fontSize: 12, color: '#92400e' }}>
                    ℹ Select a category above to see attribute-based option selectors.
                  </div>
                )}

                {options.map((o, i) => (
                  <OptionRow key={i} option={o} index={i} attrDefs={attrDefs} onChange={updateOption} onDelete={deleteOption} />
                ))}

                <button type="button" onClick={addOption}
                  style={{ height: 34, borderRadius: 7, border: '1px dashed #e8440a', background: 'transparent', color: '#e8440a', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  + Add Option
                </button>
              </div>
            )}

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 13px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {error}</div>
            )}
          </form>
        </div>

        {/* Sticky footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10, flexShrink: 0, background: '#fff' }}>
          <button type="button" onClick={onClose}
            style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Cancel
          </button>
          <button type="submit" form="productForm" disabled={saving || totalImageCount === 0}
            style={{ flex: 2, height: 42, borderRadius: 8, border: 'none', background: (saving || totalImageCount === 0) ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (saving || totalImageCount === 0) ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : '+ Add Product'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Delete confirm ────────────────────────────────────────────────────────────
function DeleteModal({ product, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false)
  const handleDelete = async () => {
    setLoading(true)
    try {
      await api.delete(`/products/delete/${product.prod_id}`)
      onDeleted(product.prod_id)
      onClose()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete product')
      setLoading(false)
    }
  }
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 700 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#fff', borderRadius: 14, zIndex: 701, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', padding: '28px', animation: 'popIn 0.2s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Delete Product?</div>
          <div style={{ fontSize: 13, color: '#9a9a94' }}>This will permanently remove <b>{product.name}</b>. This cannot be undone.</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={handleDelete} disabled={loading}
            style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#fca5a5' : '#ef4444', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Pagination button ─────────────────────────────────────────────────────────
function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Products() {
  const [products,    setProducts]    = useState([])
  const [categories,  setCategories]  = useState([])
  const [loading,     setLoading]     = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search,      setSearch]      = useState('')
  const [page,        setPage]        = useState(1)
  const [totalPages,  setTotalPages]  = useState(1)
  const [total,       setTotal]       = useState(0)
  const [modal,        setModal]        = useState(null)
  const [deleting,     setDeleting]     = useState(null)
  const [convertingId, setConvertingId] = useState(null)
  const fetchRef = useRef(0)
  const PER_PAGE = 20

  const { on } = useSocket()

  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 400); return () => clearTimeout(t) }, [searchInput])
  useEffect(() => { setPage(1) }, [search])

  useEffect(() => {
    api.get('/settings/categories').then(r => setCategories(r.data.categories || [])).catch(() => {})
  }, [])

  const fetchProducts = useCallback(async () => {
    const id = ++fetchRef.current
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, limit: PER_PAGE, ...(search && { search }) })
      const res = await api.get(`/products/my-products?${params}`)
      if (id !== fetchRef.current) return
      const data = res.data
      setProducts(data.products || data || [])
      setTotalPages(data.totalPages || 1)
      setTotal(data.total || (data.products || data || []).length)
    } catch {
      if (id !== fetchRef.current) return
      setProducts([])
    } finally { if (id === fetchRef.current) setLoading(false) }
  }, [page, search])

  useEffect(() => { fetchProducts() }, [fetchProducts])

  // Live stock update from live-stream sales
  useEffect(() => {
    const unsub = on('live:product_qty_update', ({ prod_id, quantity }) => {
      setProducts(prev => prev.map(p =>
        String(p.prod_id) === String(prod_id) ? { ...p, quantity } : p
      ))
    })
    return unsub
  }, [on])

  const handleSaved = (saved) => {
    setProducts(prev => {
      const exists = prev.find(p => p.prod_id === saved.prod_id)
      return exists
        ? prev.map(p => p.prod_id === saved.prod_id ? { ...p, ...saved } : p)
        : [saved, ...prev]
    })
    setTotal(t => modal === 'add' ? t + 1 : t)
  }
  const handleDeleted = (id) => {
    setProducts(prev => prev.filter(p => p.prod_id !== id))
    setTotal(t => Math.max(0, t - 1))
  }

  const makeStandard = async (p) => {
    setConvertingId(p.prod_id)
    try {
      const fd = new FormData()
      fd.append('product_type', 'standard')
      await api.put(`/products/update/${p.prod_id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setProducts(prev => prev.map(x => x.prod_id === p.prod_id ? { ...x, product_type: 'standard' } : x))
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to convert product')
    } finally { setConvertingId(null) }
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>My Products</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>
            {total > 0 ? `${total} product${total > 1 ? 's' : ''} listed` : 'Manage your product listings'}
          </div>
        </div>
        <button onClick={() => setModal('add')}
          style={{ height: 40, padding: '0 18px', borderRadius: 9, border: 'none', background: '#E8440A', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 2px 8px rgba(232,68,10,0.2)', display: 'flex', alignItems: 'center', gap: 6 }}>
          + Add Product
        </button>
      </div>

      {/* Search toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search products…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <button onClick={fetchProducts} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{total} products</div>
      </div>

      {/* Product grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 14 }}>
          {[1,2,3,4,5,6].map(i => <div key={i} style={{ height: 270, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, opacity: 0.5 }} />)}
        </div>
      ) : products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>No products yet</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 20 }}>Add your first product to start selling on Kudos Live.</div>
          <button onClick={() => setModal('add')}
            style={{ height: 40, padding: '0 24px', borderRadius: 9, border: 'none', background: '#E8440A', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            + Add First Product
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 14 }}>
          {products.map(p => {
            const img        = parseImg(p.images)
            const outOfStock = p.quantity != null && Number(p.quantity) === 0
            return (
              <div key={p.prod_id} style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {/* Image */}
                <div style={{ height: 160, background: '#f3f3f0', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                  {img
                    ? <img src={img} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>📦</div>
                  }
                  {outOfStock && (
                    <div style={{ position: 'absolute', top: 8, right: 8, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace" }}>OUT OF STOCK</div>
                  )}
                  {p.product_type === 'quick_live' && (
                    <div style={{ position: 'absolute', top: 8, left: 8, background: '#E8440A', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace" }}>⚡ QUICK LIVE</div>
                  )}
                  {p.has_variants && (
                    <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace" }}>🎨 OPTIONS</div>
                  )}
                </div>

                {/* Info */}
                <div style={{ padding: '12px 14px', flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', marginBottom: 8 }}>{p.category || '—'}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 14, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{rwf(p.price)}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, background: outOfStock ? '#fef2f2' : '#f0fdf4', color: outOfStock ? '#b91c1c' : '#15803d' }}>
                      {p.quantity != null ? `${p.quantity} in stock` : 'In stock'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ padding: '10px 14px', borderTop: '1px solid #f3f3f0', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {p.product_type === 'quick_live' && (
                    <button
                      onClick={() => makeStandard(p)}
                      disabled={convertingId === p.prod_id}
                      style={{ width: '100%', height: 30, borderRadius: 7, border: '1px solid #fdd0c0', background: '#fff3f0', color: '#e8440a', fontSize: 11, fontWeight: 700, cursor: convertingId === p.prod_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      {convertingId === p.prod_id ? 'Converting…' : '📦 Make Standard'}
                    </button>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setModal(p)}
                      style={{ flex: 1, height: 32, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      Edit
                    </button>
                    <button onClick={() => setDeleting(p)}
                      style={{ height: 32, padding: '0 12px', borderRadius: 7, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Modals */}
      {modal && (
        <ProductModal
          product={modal === 'add' ? null : modal}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
      {deleting && (
        <DeleteModal product={deleting} onClose={() => setDeleting(null)} onDeleted={handleDeleted} />
      )}
    </div>
  )
}
